/**
 * @returns {boolean}
 */
function isQaModeActive() {
  const qa = window.OtterQaMode;

  if (qa?.syncSessionFromUrl) {
    qa.syncSessionFromUrl();
  }

  if (qa?.isEnabled) {
    return qa.isEnabled();
  }

  return new URLSearchParams(window.location.search).get('qa_mode') === 'true';
}

/**
 * Signals the country-language drawer to pre-select the visitor’s detected country
 * (or closest dropdown match). Cleared when the selector consumes it.
 * @param {string} isoCode
 */
function setGeoPreselectCountry(isoCode) {
  if (!isoCode || typeof isoCode !== "string") return;
  const code = isoCode.trim().toUpperCase();
  const w = /** @type {Window & { __otterGeoPreselectCountry?: string, __otterGeoDetectedCountry?: string }} */ (window);
  w.__otterGeoPreselectCountry = code;
  w.__otterGeoDetectedCountry = code;
  try {
    sessionStorage.setItem('otter_geo_detected_country', code);
  } catch (e) {}
}

/**
 * Metafield JSON uses bare hosts or paths (`staging.otterbox.eu/en-eu`). `new URL()` requires a scheme.
 * @param {string} raw
 * @returns {string | null}
 */
function hostnameFromStoreUrl(raw) {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    return new URL(withScheme).hostname;
  } catch {
    return null;
  }
}

/**
 * @param {unknown} languageField
 * @returns {string[]} lowercased language codes (e.g. en, de)
 */
function languageCodesFromStore(languageField) {
  if (!languageField) return [];
  if (Array.isArray(languageField)) {
    return languageField
      .map((entry) => {
        if (entry && typeof entry === "object" && "code" in entry) {
          return String(/** @type {{ code?: string }} */(entry).code || "")
            .trim()
            .toLowerCase();
        }
        return String(entry).trim().toLowerCase();
      })
      .filter(Boolean);
  }
  if (typeof languageField === "string") {
    return languageField
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
  }
  return [];
}

// ISO country codes for mainland Europe + Ireland (excludes GB/UK and ANZ, which are their
// own dedicated markets). Used to recognize European visitors who are already on the EMEA
// storefront, whose per-locale store rows don't always resolve to the browsed hostname.
const EUROPE_ISO_CODES = new Set([
  "AD", "AL", "AT", "AX", "BA", "BE", "BG", "BY", "CH", "CY", "CZ", "DE", "DK", "EE", "ES",
  "FI", "FO", "FR", "GI", "GR", "HR", "HU", "IE", "IS", "IT", "LI", "LT", "LU", "LV", "MC",
  "MD", "ME", "MK", "MT", "NL", "NO", "PL", "PT", "RO", "RS", "SE", "SI", "SK", "SM", "UA",
  "VA",
]);

// Languages the EMEA storefront actually offers as distinct locales. Used to decide whether
// a European visitor's browser language warrants a language-switch prompt.
const EU_AVAILABLE_LANGUAGES = new Set(["en", "fr", "de", "nl", "sv"]);

class GeoLocation {
  constructor() {
    this.url = "/browsing_context_suggestions.json";
    this.userData = null;
    this.BYPASS_PARAM = "source";
    this.BYPASS_VALUE = "na_store";
    this.init();
  }

  async init() {
    await this.fetchStores();
    this.afterFetch();
  }

  async fetchStores() {
    try {
      const response = await fetch(this.url, {
        method: "GET",
        headers: { "Accept": "application/json" }
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      this.userData = data;
    } catch (error) {
      console.error("Fetch error:", error);
    }
  }

  afterFetch() {
    if (!this.userData || !window.marketRouting) return;

    if (isQaModeActive()) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const isBypassed = urlParams.get(this.BYPASS_PARAM) === this.BYPASS_VALUE;

    const detectedISO = this.userData?.detected_values?.country?.handle?.toUpperCase();
    if (!detectedISO) return;

    const w = /** @type {Window & { __otterGeoDetectedCountry?: string }} */ (window);
    w.__otterGeoDetectedCountry = detectedISO;
    try {
      sessionStorage.setItem('otter_geo_detected_country', detectedISO);
    } catch (e) {}

    const activeShopifyCountry = String(window.Shopify?.country || '').trim().toUpperCase();
    const currentHostname = window.location.hostname;

    // Detect development, preview, or *.myshopify.com environments
    const isPreviewOrDev =
      currentHostname.includes('myshopify.com') ||
      currentHostname === 'localhost' ||
      currentHostname === '127.0.0.1';

    // 1. Skip modal if visitor is already on a matching active session country
    if (activeShopifyCountry && detectedISO === activeShopifyCountry && (isPreviewOrDev || !this.getCookie('declined_redirect'))) {
      console.log("Visitor ISO matches active session country. Suppressing geo prompt.");
      return;
    }

    // 2. Visitor detected in Europe while already on the EMEA/Ireland storefront: per-locale
    // store rows (DE, FR, NL, SE, etc.) don't always resolve back to the currently browsed
    // hostname below, so this is handled here instead. Only prompt when the visitor's browser
    // language is one of our other supported European languages and differs from what's
    // currently being served (e.g. a French/German/Dutch/Swedish speaker on the English root) —
    // otherwise they're already on the right site and shouldn't be nagged again.
    const storeRegion = String(window.OtterVars?.MarketSettings?.storeRegion || '').trim().toLowerCase();
    const isEmeaStorefront = storeRegion === 'emea' || storeRegion === 'ireland';
    if (isEmeaStorefront && EUROPE_ISO_CODES.has(detectedISO)) {
      const activeLocale = String(window.Shopify?.locale || '').trim().toLowerCase();
      const browserLangFull = navigator.language || navigator.userLanguage;
      const browserLangCode = String(browserLangFull || '').split('-')[0].toLowerCase();

      const offerLanguageSwitch =
        browserLangCode
        && browserLangCode !== activeLocale
        && EU_AVAILABLE_LANGUAGES.has(browserLangCode);

      if (!offerLanguageSwitch) {
        console.log("Visitor is in Europe and already viewing a matching/unsupported language on the EMEA storefront. Suppressing geo prompt.");
        return;
      }

      console.log("Visitor is in Europe on the EMEA storefront with a different supported language. Offering language switch.", { browserLangCode, activeLocale });
      this.triggerModal(detectedISO, isBypassed);
      return;
    }

    const stores = window.marketRouting.stores || [];

    // Find stores that contain the detected ISO
    const matchedStores = stores.filter(store =>
      store.isoCode
        .split(",")
        .map(code => code.trim().toUpperCase())
        .includes(detectedISO)
    );

    // If no ISO match at all
    if (matchedStores.length === 0) {
      console.log("iso not matched");
      this.triggerModal(detectedISO, isBypassed);
      return;
    }

    // Check hostname match (allowing preview/dev domains if Shopify country matches)
    const hostnameMatched = matchedStores.some((store) => {
      const storeHostname = hostnameFromStoreUrl(store.url);
      return storeHostname === currentHostname || (isPreviewOrDev && activeShopifyCountry === detectedISO);
    });

    if (!hostnameMatched) {
      console.log("hostname not matched");
      this.triggerModal(detectedISO, isBypassed);
      return; // Exit early after triggering modal
    }

    const browserLangFull = navigator.language || navigator.userLanguage; // e.g. en-US
    const browserLangCode = browserLangFull.split("-")[0].toLowerCase();

    // Get stores that match BOTH ISO and hostname
    const hostnameMatchedStores = matchedStores.filter((store) => {
      const storeHostname = hostnameFromStoreUrl(store.url);
      return storeHostname === currentHostname || isPreviewOrDev;
    });

    // Check language match
    if (hostnameMatchedStores.length > 0) {
      const languageMatched = hostnameMatchedStores.some((store) => {
        const codes = languageCodesFromStore(store.language);
        if (codes.length === 0) return true;
        return codes.includes(browserLangCode);
      });

      if (!languageMatched) {
        console.log("language not matched", {
          browserLangCode,
          hostnameMatchedStores,
        });

        this.triggerModal(detectedISO, isBypassed);
      } else {
        console.log("language matched", browserLangCode);
      }
    }
  }

  /**
   * Helper to handle the UI trigger for the modal
   * @param {string} isoCode
   * @param {boolean} isBypassed
   */
  triggerModal(isoCode, isBypassed) {
    if (isQaModeActive()) {
      return;
    }

    if (!this.getCookie('declined_redirect')) {
      setGeoPreselectCountry(isoCode);
      if (!isBypassed) {
        document.querySelector('.menu-drawer__menu-item--country')?.click();
      }
    }
  }

  /**
   * Gets a cookie value by name.
   *
   * @param {string} name - The cookie name.
   * @returns {string|null} The cookie value or null if not found.
   */
  getCookie = (name) => {
    const cookies = document.cookie ? document.cookie.split('; ') : [];

    for (const cookie of cookies) {
      const [key, ...rest] = cookie.split('=');
      if (decodeURIComponent(key) === name) {
        return decodeURIComponent(rest.join('='));
      }
    }

    return null;
  };
}

document.addEventListener("DOMContentLoaded", () => {
  new GeoLocation();
});